import { ModuleService } from "@services/api/Module.service";
import { useQuery } from "@tanstack/react-query";
import { ENV } from "config/ENV.config";
import Dropdown from "react-bootstrap/Dropdown";
import { appIcon } from "utility/appIcons";

type PurchasedModule = { id: string; title: string; code: string; appKey?: string; hostOrigin?: string };

/** One cell of the 9-dot grid. `url` is empty for the app you are already in. */
type Tile = { id: string; title: string; subtitle: string; icon: string; url: string; isCurrent: boolean };

/** Portal is the suite's administration console, and is labelled so in every app's switcher. */
const PORTAL_TITLE = "Administration";

const toUrl = (host?: string) => (!host ? "" : /^https?:\/\//i.test(host) ? host : `https://${host}`);

// Resolved like Dashboard's "Open console": the env-driven console URL (localhost in dev), else
// the module's host_origin.
const consoleUrl = (m: PurchasedModule) => ENV.consoles?.[m.code?.toLowerCase() || ""] || toUrl(m.hostOrigin);

// The same grid the other apps show (hrm's QuickLinks): the apps this company has purchased, then
// Administration. Here Administration is the app you are in, so it is listed but disabled, rather
// than silently missing. A module with nowhere to open is dropped.
const buildTiles = (list: PurchasedModule[]): Tile[] => [
  ...list
    .map((m) => ({ id: m.id, title: m.title, subtitle: "Module", icon: appIcon(m.code), url: consoleUrl(m), isCurrent: false }))
    .filter((t) => t.url),
  { id: "PORTAL", title: PORTAL_TITLE, subtitle: "Current module", icon: "gear-wide-connected", url: "", isCurrent: true },
];

// Module scope: a component declared inside the render body is a new type on every render.
const TileBody = ({ tile }: { tile: Tile }) => (
  <>
    <i className={`bi bi-${tile.icon}`} />
    <span className="erp-switcher__title">{tile.title}</span>
    <span className="erp-switcher__sub">{tile.subtitle}</span>
  </>
);

/** The header's 9-dot module switcher. */
const ModuleSwitcher = () => {
  // Fetched each time the menu opens, so an app activated a moment ago is already there.
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["module-switcher"],
    queryFn: ModuleService.getPurchasedModules,
    enabled: false,
  });
  // A module-list outage must still leave the grid (and Administration) showing.
  const tiles = buildTiles(data?.body || []);

  return (
    <Dropdown as="li" className="nav-item erp-switcher" align="end" onToggle={(open) => open && refetch()}>
      <Dropdown.Toggle as="button" bsPrefix="erp-header-btn" className="nav-link" aria-label="Switch module" title="Switch module">
        <i className="bi bi-grid-3x3-gap-fill" />
      </Dropdown.Toggle>

      <Dropdown.Menu className="erp-switcher__menu p-0">
        {isFetching && !data ? (
          <div className="py-4 text-center small text-body-secondary">Loading…</div>
        ) : (
          <div className="row g-0">
            {tiles.map((tile) => (
              <div className="col-6" key={tile.id}>
                {tile.isCurrent ? (
                  // A plain cell, dimmed: a switcher offering to go where you already are reads as a dead link.
                  <div className="erp-switcher__tile is-current" aria-disabled="true" title="You are here">
                    <TileBody tile={tile} />
                  </div>
                ) : (
                  <a href={tile.url} target="_blank" rel="noreferrer" className="erp-switcher__tile">
                    <TileBody tile={tile} />
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ModuleSwitcher;
